/**
 * 実スペクトルからのカテゴリ推定と、その結果の共有ストア。
 * サムネイルの配色・動きはこの推定だけで決まる。
 *
 * 特徴量の供給元は 2 つあり、どちらも spectrumFeatures で同じ尺度に揃えてある。
 *   - offlineSpectrum: 先頭数十秒を Range 取得してデコードしたもの（再生前）
 *   - audioAnalyser:   再生中の AnalyserNode（オフライン解析が使えない場合の保険）
 *
 * 結果は音源ごとにキャッシュし、次に開いたときは取得も解析もせず即座に出す。
 *
 * ■ しきい値について
 * SPECTRUM_TUNING は実音源の分布から決めてある。localStorage に
 * 'soremo:audioSpectrumDebug' = '1' を入れると、判定に使った特徴量と各スコアが
 * コンソールに出る。ラベル付きフォルダに対する一括再計測は
 * scripts/checkAudioClassifier.ts で行える。
 */

import { MeasuredCategory } from './audioCategory';
import { SpectrumFrame } from './spectrumFeatures';

/**
 * 特徴量を渡してくる側のフレームレート。時間方向の指標（無音率・オンセット間隔）
 * はこの値を前提に較正してあるので、供給側は必ずこのレートに合わせること。
 */
export const FRAME_RATE = 30;
/**
 * 推定を確定させるのに必要な有音フレーム数（30fps で約 1.5 秒）。
 *
 * 効果音は 2〜5 秒しかないものが多い。ここを長く取ると効果音が永久に
 * 判定不能になり、全部 neutral のまま残ってしまう。
 */
const MIN_FRAMES = 40;
/** 尺が短い音源で要求フレーム数を下げるときの下限。これ未満では判定しない */
const MIN_FRAMES_ABSOLUTE = 10;
/** 尺のうち有音であることを期待する割合。効果音は無音の間が長い */
const SHORT_SOURCE_FRAME_RATIO = 0.15;
/** これ未満のフレームは無音とみなし、統計に入れない */
const MIN_LEVEL = 0.12;
/** カテゴリを確定させる最低スコア。届かなければ neutral のままにする */
const CONFIDENCE_THRESHOLD = 0.55;

/**
 * しきい値。実音源（楽曲 14 / 発話 4 / 効果音 6）の実測分布から決めてある。
 * scripts/checkAudioClassifier.ts でラベル付きフォルダに対して再計測できる。
 *
 * ■ 効かなかった指標について
 * スペクトル平坦度と重心は、当初は音楽と効果音を分ける主役に据えていたが、
 * 実測すると楽曲が 0.03〜0.64 と効果音の範囲にすっぽり重なり、区別に使え
 * なかった。重心は外し、平坦度は 0.21〜0.23 に固まる声の補助としてだけ残す。
 * 音節の速さ（3-6Hz の変調）も試したが、楽曲と発話で分布が重なり効かなかった。
 *
 * 効くのは時間方向の指標だった。無音率（鳴り続けるか、途切れるか）が声と
 * 音楽をほぼ分離し、尺の短さが効果音を拾う。
 */
const SPECTRUM_TUNING = {
  /**
   * 無音率。声は語間・文間で必ず落ちる（実測 0.20-0.29）。
   * 楽曲は鳴り続ける（実測 0.01-0.09）。この 2 つを分ける最大の手がかり
   */
  voicePauseBand: [0.15, 0.45] as const,
  pauseFalloff: 0.06,
  /** 楽曲とみなす「途切れなさ」。ここを超えると連続音とみなさない */
  musicContinuityRange: [0.1, 0.24] as const,
  /** 効果音は鳴っていない時間が長い */
  effectPauseRange: [0.2, 0.45] as const,
  /**
   * 超低域（20-120Hz）のエネルギー比。キック・ベースの有無。
   * 声にはほぼ無い（実測 0.007-0.085）
   */
  musicSubBassRange: [0.03, 0.12] as const,
  voiceSubBassRange: [0.06, 0.16] as const,
  /**
   * ここまで低域が出ていれば声ではない（実測した発話の上限は 0.085）。
   * 休符の多い低音の効いた楽曲を声と誤らないための減点
   */
  nonVoiceSubBassRange: [0.16, 0.3] as const,
  /** 発話帯域（300-3400Hz）のエネルギー比。声は実測 0.46-0.52 に収まる */
  voiceSpeechBand: [0.38, 0.68] as const,
  speechBandFalloff: 0.08,
  /** スペクトル平坦度。声は実測 0.21-0.23 と狭い範囲に固まる */
  voiceFlatnessBand: [0.1, 0.34] as const,
  flatnessFalloff: 0.06,
  /** ここを超えるとノイズ的とみなし、声のスコアを減点する */
  noisyFlatnessRange: [0.35, 0.6] as const,
  /** オンセット（flux のピーク）の間隔の均等さ。音楽の手がかり */
  onsetRegularityRange: [0.25, 0.6] as const,
  /** flux をオンセットとみなすしきい値（平均 + 標準偏差 の何倍か） */
  onsetSigma: 1,
  /** 尺（秒）。効果音は短く、楽曲は長い */
  effectDurationRange: [8, 25] as const,
  musicDurationRange: [20, 60] as const,
};

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

const ramp = (value: number, lo: number, hi: number): number =>
  hi === lo ? (value >= hi ? 1 : 0) : clamp01((value - lo) / (hi - lo));

const band = (value: number, lo: number, hi: number, falloff: number): number =>
  Math.min(ramp(value, lo - falloff, lo), 1 - ramp(value, hi, hi + falloff));

export interface SpectrumEstimate {
  category: MeasuredCategory;
  confidence: number;
}

export interface SpectrumEstimator {
  /** 1 フレーム分の特徴量を取り込む */
  push(_frame: SpectrumFrame): void;
  /** 十分なフレームが溜まっていれば推定を返す。まだなら null */
  estimate(): SpectrumEstimate | null;
  /** 判定に使った中間値。しきい値の追い込み用（フレーム数が足りなくても返す） */
  inspect(): SpectrumStats;
}

/** estimate() が内部で見ている値。しきい値調整のために外へ出す */
export interface SpectrumStats {
  frames: number;
  speechBand: number;
  subBass: number;
  flatness: number;
  onsetRegularity: number;
  pauseRatio: number;
  scores: Record<MeasuredCategory, number>;
}

const isDebugEnabled = (): boolean => {
  try {
    return window.localStorage.getItem('soremo:audioSpectrumDebug') === '1';
  } catch {
    return false;
  }
};

/**
 * スペクトルを累積してカテゴリを推定する。
 * 平均だけでなく時間方向の並びも見るので、フレームの順序を保って push すること。
 *
 * @param durationSec 音源全体の長さ。効果音かどうかの判断に効くので、判って
 *   いれば渡す。先頭だけ切り出して解析している場合など、全体長が不明なときは
 *   null にすること（尺の項が中立になる）
 */
export function createSpectrumEstimator(durationSec: number | null = null): SpectrumEstimator {
  let frames = 0;
  let speechBandSum = 0;
  let subBassSum = 0;
  let flatnessSum = 0;

  // オンセット間隔の均等さを取るための flux 履歴（フレーム番号ベース）
  const fluxHistory: number[] = [];
  // 音量の時間変化。無音率に使う（無音フレームも入れて時間軸を保つ）
  const levelHistory: number[] = [];

  return {
    push(frame: SpectrumFrame): void {
      levelHistory.push(frame.level);
      if (frame.level < MIN_LEVEL) {
        fluxHistory.push(0);
        return;
      }
      frames += 1;
      speechBandSum += frame.speechBand;
      subBassSum += frame.subBass;
      flatnessSum += frame.flatness;
      fluxHistory.push(frame.flux);
    },

    inspect(): SpectrumStats {
      return computeStats();
    },

    estimate(): SpectrumEstimate | null {
      if (frames < requiredFrames()) return null;
      const stats = computeStats();
      const best = (Object.keys(stats.scores) as Array<keyof typeof stats.scores>).reduce((a, b) =>
        stats.scores[b] > stats.scores[a] ? b : a,
      );

      if (isDebugEnabled()) console.info('[audioSpectrum] 判定', { ...stats, best });

      if (stats.scores[best] < CONFIDENCE_THRESHOLD) return null;
      return { category: best, confidence: stats.scores[best] };
    },
  };

  /**
   * 判定に要求する有音フレーム数。
   *
   * 短い音源では待っても増えないので、尺が判っているならそれに応じて下げる。
   * ここを一律 MIN_FRAMES にすると、数秒の効果音が永久に判定不能のまま
   * neutral で残ってしまう。
   */
  function requiredFrames(): number {
    if (durationSec === null) return MIN_FRAMES;
    const available = durationSec * FRAME_RATE * SHORT_SOURCE_FRAME_RATIO;
    return Math.max(MIN_FRAMES_ABSOLUTE, Math.min(MIN_FRAMES, Math.floor(available)));
  }

  function computeStats(): SpectrumStats {
    const divisor = Math.max(1, frames);
    const speechBand = speechBandSum / divisor;
    const subBass = subBassSum / divisor;
    const flatness = flatnessSum / divisor;
    const onsetRegularity = computeOnsetRegularity(fluxHistory);
    const pauseRatio = computePauseRatio(levelHistory);

    const t = SPECTRUM_TUNING;
    const regular = ramp(onsetRegularity, ...t.onsetRegularityRange);
    // 尺が判らない場合（再生中の推定など）は尺の項を中立にする
    const knownDuration = durationSec !== null;
    const isShort = knownDuration ? 1 - ramp(durationSec, ...t.effectDurationRange) : 0.5;
    const isLong = knownDuration ? ramp(durationSec, ...t.musicDurationRange) : 0.5;

    // どのスコアも重み付き和にしてある。以前はここに「トーン的か」を全体に
    // 掛ける形の関門を置いていたが、実測すると平坦度は楽曲と効果音で重なる
    // ため、1 つのしきい値のずれが音楽と声の両方をゼロに潰していた。
    // 加算なら 1 つの指標が外れても他が支えられる。

    // 声: 語間で途切れ、低域が無く、発話帯域に集中し、平坦度が中庸
    const voiceEvidence = clamp01(
      0.45 * band(pauseRatio, ...t.voicePauseBand, t.pauseFalloff) +
        0.2 * (1 - ramp(subBass, ...t.voiceSubBassRange)) +
        0.2 * band(speechBand, ...t.voiceSpeechBand, t.speechBandFalloff) +
        0.15 * band(flatness, ...t.voiceFlatnessBand, t.flatnessFalloff),
    );
    // 声から明確に遠いものは減点する。無音率と発話帯域だけを足していくと、
    // 間の多い効果音や、休符のある低音の効いた楽曲が声に化けるため。
    // 関門にはせず係数の下限を残し、他の指標が効く余地を残してある
    const voice =
      voiceEvidence *
      (1 - 0.6 * ramp(flatness, ...t.noisyFlatnessRange)) *
      (1 - 0.6 * ramp(subBass, ...t.nonVoiceSubBassRange));

    // 音楽: 鳴り続け、低域があり、拍が等間隔に並び、尺が長い
    const music = clamp01(
      0.4 * (1 - ramp(pauseRatio, ...t.musicContinuityRange)) +
        0.25 * ramp(subBass, ...t.musicSubBassRange) +
        0.2 * regular +
        0.15 * isLong,
    );

    // 効果音: 短く、鳴っていない時間が長く、拍が並ばない
    const soundEffect = clamp01(
      0.5 * isShort + 0.25 * ramp(pauseRatio, ...t.effectPauseRange) + 0.25 * (1 - regular),
    );

    return {
      frames,
      speechBand,
      subBass,
      flatness,
      onsetRegularity,
      pauseRatio,
      scores: { voice, music, sound_effect: soundEffect },
    };
  }
}

/**
 * 音量が平均を大きく下回るフレームの割合（0-1）。
 * 発話は語間・文間で頻繁に落ち込むが、楽曲は鳴り続ける。
 */
function computePauseRatio(level: number[]): number {
  const n = level.length;
  if (n === 0) return 0;
  const mean = level.reduce((a, b) => a + b, 0) / n;
  if (mean <= 0) return 0;
  const threshold = mean * 0.5;
  let quiet = 0;
  for (let i = 0; i < n; i++) if (level[i] < threshold) quiet += 1;
  return quiet / n;
}

/** オンセット（flux のピーク）の間隔の均等さ（0-1）。等間隔に刻まれるほど 1 */
function computeOnsetRegularity(flux: number[]): number {
  const n = flux.length;
  if (n < 60) return 0;

  const mean = flux.reduce((a, b) => a + b, 0) / n;
  const variance = flux.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
  const threshold = mean + Math.sqrt(variance) * SPECTRUM_TUNING.onsetSigma;
  if (threshold <= 0) return 0;

  const intervals: number[] = [];
  let last = -1;
  for (let i = 0; i < n; i++) {
    if (flux[i] < threshold) continue;
    // 同一の立ち上がりの二重検出を弾く（30fps で 100ms 相当）
    if (last >= 0 && i - last < 3) continue;
    if (last >= 0) intervals.push(i - last);
    last = i;
  }
  if (intervals.length < 4) return 0;

  const intervalMean = intervals.reduce((a, b) => a + b, 0) / intervals.length;
  if (intervalMean <= 0) return 0;
  const intervalVariance =
    intervals.reduce((a, b) => a + (b - intervalMean) ** 2, 0) / intervals.length;
  return clamp01(1 - Math.sqrt(intervalVariance) / intervalMean);
}

// =============================================================================
// 共有ストア（音源キー → 実スペクトル由来のカテゴリ）
// =============================================================================

const STORAGE_KEY = 'soremo:audioCategoryCache';
/** localStorage に残す件数の上限。超えたら古いものから捨てる */
const STORAGE_LIMIT = 400;

const listeners = new Set<() => void>();

/**
 * 署名付き URL のクエリは開くたびに変わるので、パスだけをキーにする。
 * これで再訪時に取得も解析もせずキャッシュが当たる。
 */
const normalizeKey = (key: string): string => key.split('?')[0];

const loadPersisted = (): Map<string, MeasuredCategory> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return new Map();
    return new Map(Object.entries(parsed as Record<string, MeasuredCategory>));
  } catch {
    return new Map();
  }
};

const categoryByKey = loadPersisted();

const persist = (): void => {
  try {
    // Map は挿入順を保つので、先頭（古い方）から溢れた分を落とす
    const entries = Array.from(categoryByKey.entries()).slice(-STORAGE_LIMIT);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    // 容量超過やプライベートモードでの失敗は無視（メモリ上のキャッシュは生きている）
  }
};

/** 推定結果を記録して購読者に通知する（同じ結果なら何もしない） */
export function setSpectrumCategory(
  key: string,
  category: MeasuredCategory,
): void {
  const normalized = normalizeKey(key);
  if (categoryByKey.get(normalized) === category) return;
  categoryByKey.set(normalized, category);
  persist();
  listeners.forEach(listener => listener());
}

export function getSpectrumCategory(
  key: string | null | undefined,
): MeasuredCategory | null {
  if (!key) return null;
  return categoryByKey.get(normalizeKey(key)) ?? null;
}

export function subscribeSpectrumCategory(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
